import { useEffect, useState } from 'react';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SttPagination } from './SttPagination';
import { SttNoResults } from './SttNoResults';
import type { ActivityAttendance } from '../types/models/ActivityAssistance';
import { GetActivityStudentAssistences200 } from '../types/api/paths/Student';

type Column = {
    title: string;
    field: string;
    type: 'text';
};

type SttAttendanceTableProps = {
    columns: Column[];
    data: GetActivityStudentAssistences200;
    onClickCell: (row: ActivityAttendance, col: Column) => void;
    onChangePageCallback: (p: number) => void;
};

export function SttAttendanceTable({
    columns,
    onChangePageCallback,
    data,
    onClickCell,
}: SttAttendanceTableProps) {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [internalData, setInternalData] = useState<
        GetActivityStudentAssistences200 | undefined
    >(data);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const onChangePage = (p: number) => {
        setPage(p);
        onChangePageCallback(p);
    };

    useEffect(() => {
        if (data?.rows) {
            setInternalData(data);

            if (page === 1 && data?.rows?.length < data?.count) {
                setRowsPerPage(data?.rows.length);
            }
        }
    }, [data, page]);

    function getLetterByAttendanceStatus(status: ActivityAttendance['status']) {
        switch (status) {
            case 'ASSIST':
                return 'A';
            case 'MISSED':
                return 'X';
            case 'RECOVERABLE':
                return 'R';
            case 'RECOVERER':
                return 'Rc';
            case 'RECOVERED':
                return 'Rd';
            default:
                return null;
        }
    }

    function getStatusColor(status: ActivityAttendance['status']) {
        switch (status) {
            case 'ASSIST':
                return 'bg-green-100 text-green-700';
            case 'MISSED':
                return 'bg-red-100 text-red-700';
            case 'RECOVERABLE':
                return 'bg-amber-100 text-amber-700';
            case 'RECOVERER':
                return 'bg-blue-100 text-blue-700';
            case 'RECOVERED':
                return 'bg-purple-100 text-purple-700';
            default:
                return '';
        }
    }

    return (
        <>
            <Box display="flex" flexDirection="row-reverse">
                <SttPagination
                    page={page}
                    pages={Math.ceil(data.count / rowsPerPage)}
                    count={data?.count}
                    onChangePage={(p: number) => {
                        onChangePage(p);
                    }}
                />
            </Box>

            <Box display="flex" flexWrap="nowrap">
                {/* Los nombres de los alumnos se pintan por separado a la izquierda, para que sean independientes del scroll de la tabla de días */}
                <Box
                    display="flex"
                    flexWrap="nowrap"
                    flexDirection="column"
                    alignItems="stretch"
                    className="pr-2 border-r border-gray-200"
                >
                    <div className="h-[37px] flex items-center font-semibold text-gray-700">
                        {t('Name')}
                    </div>
                    {internalData?.rows?.map((row: ActivityAttendance) => (
                        <div
                            key={row.id}
                            className="h-[33px] flex items-center whitespace-nowrap text-sm"
                        >
                            <span>{row?.user?.mship?.fullName}</span>
                        </div>
                    ))}
                </Box>
                {/* A continuación se pinta la tabla de días */}
                <Box flex={1} style={{ overflowX: 'auto' }}>
                    <TableContainer>
                        <Table size="small" aria-label="attendance table">
                            <TableHead>
                                <TableRow>
                                    {columns?.map((col: Column) => (
                                        <TableCell
                                            key={col.title}
                                            align="center"
                                            className="font-semibold text-gray-700 border border-gray-300 min-w-[36px] p-1"
                                        >
                                            {col.title}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {internalData?.rows?.map(
                                    (row: ActivityAttendance) => (
                                        <TableRow key={row.id}>
                                            {columns?.map((col: Column) => {
                                                const assistenceFound =
                                                    row.assistences.find(
                                                        (assistence) =>
                                                            new Date(
                                                                assistence.class
                                                                    .ini,
                                                            ).getDate() ===
                                                            parseInt(col.field),
                                                    );
                                                const assistenceLetter =
                                                    assistenceFound
                                                        ? getLetterByAttendanceStatus(
                                                              assistenceFound.status,
                                                          )
                                                        : null;
                                                const statusColor =
                                                    assistenceFound
                                                        ? getStatusColor(
                                                              assistenceFound.status,
                                                          )
                                                        : '';
                                                return assistenceFound ? (
                                                    <TableCell
                                                        key={col.title}
                                                        onClick={() =>
                                                            onClickCell?.(
                                                                row,
                                                                col,
                                                            )
                                                        }
                                                        align="center"
                                                        className={`border border-gray-300 min-w-[36px] h-[33px] p-1 cursor-pointer transition-colors duration-150 hover:brightness-90 hover:border-gray-400 relative font-semibold ${statusColor}`}
                                                    >
                                                        {assistenceLetter}
                                                        {assistenceFound.notes &&
                                                        assistenceFound.notes !==
                                                            '' ? (
                                                            <div className="absolute top-0 right-0 w-0 h-0 border-t-[6px] border-t-blue-500 border-l-[6px] border-l-transparent" />
                                                        ) : null}
                                                    </TableCell>
                                                ) : (
                                                    <TableCell
                                                        key={col.title}
                                                        align="center"
                                                        className="border border-gray-300 min-w-[36px] h-[33px] p-1 bg-gray-50"
                                                    >
                                                        &nbsp;
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ),
                                )}

                                {internalData?.count === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={100}>
                                            <SttNoResults />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>

            <Box display="flex" flexDirection="row-reverse">
                <SttPagination
                    page={page}
                    pages={Math.ceil(data.count / rowsPerPage)}
                    count={data?.count}
                    onChangePage={(p: number) => {
                        onChangePage(p);
                    }}
                />
            </Box>
        </>
    );
}
